import { RowCrudStatus } from "../../common/types";
import {
  UserAttachmentEdition,
  UserAttachmentEdition_CreateInput,
  UserAttachmentEdition_UpdateInput,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getFileVariableValue,
  getNotNullFileVariableValue,
} from "../../common/utils";
import { blankToNull } from "../../../common/blankToNull";

let uniqueCounter = 1;
export type UserAttachmentEditionValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  file: string;
  file_data: FileCRUDType;
  __file_files: FileCRUDType[];
  notes: string;
};

export type UserAttachmentEditionErrorsType = {
  name?: { message: string; fieldLabel: string };
  file?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
};

export function UserAttachmentEditionValuesInit(): UserAttachmentEditionValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    file: "",
    file_data: fileCRUDInit(),
    __file_files: [],
    notes: "",
  };
}

export type UserAttachmentEditionType =
  | UserAttachmentEdition
  | UserAttachmentEditionValuesType;

export function userAttachmentEditionDisplayId(
  source: Partial<UserAttachmentEdition>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getUserAttachmentEditionValues(
  source: UserAttachmentEditionType,
  _langStore: LangStore,
  _duplicated: boolean = false
): UserAttachmentEditionValuesType {
  const entityValues = UserAttachmentEditionValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : userAttachmentEditionDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.__file_files =
    source.file_data && !_duplicated
      ? [
          {
            id:
              "id" in source.file_data && source.file_data.id
                ? source.file_data.id
                : `generated_${uniqueCounter++}`,
            name: source.file_data.name,
            description: source.file_data.description || undefined,
            size: source.file_data.size || 0,
            formattedSize: source.file_data.size
              ? formatBytes(source.file_data.size)
              : "",
            mimetype: source.file_data.mimetype,
            url: source.file_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.file = source.file && !_duplicated ? source.file : "";
  entityValues.file_data =
    source.file_data && "__status__" in source.file_data
      ? source.file_data
      : source.file_data
      ? {
          id: ("id" in source.file_data && source.file_data.id
            ? source.file_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.file_data.name,
          description: source.file_data.description || undefined,
          size: source.file_data.size || 0,
          formattedSize: source.file_data.size
            ? formatBytes(source.file_data.size)
            : "",
          mimetype: source.file_data.mimetype,
          url: source.file_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.notes = source.notes || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getUserAttachmentEditionUpdateInput(
  values: UserAttachmentEditionValuesType
): UserAttachmentEdition_UpdateInput {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    file: getFileVariableValue(values.__file_files),
    notes: blankToNull(values.notes),
  };
}

export function getUserAttachmentEditionCreateInput(
  values: UserAttachmentEditionValuesType
): UserAttachmentEdition_CreateInput {
  return {
    name: values.name,
    file: getNotNullFileVariableValue(values.__file_files),
    notes: blankToNull(values.notes),
  };
}
